import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Home, MailCheck, CheckCircle, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { authAPI } from '../lib/api';
import {
  parseVerificationToken,
  mapVerifyErrorToState,
  VERIFY_STATE_IDLE,
  VERIFY_STATE_MISSING,
  VERIFY_STATE_LOADING,
  VERIFY_STATE_SUCCESS,
  VERIFY_STATE_INVALID,
  VERIFY_STATE_ERROR,
} from '../lib/emailVerificationFlow';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { refreshUser } = useAuth();

  const token = parseVerificationToken(searchParams);

  // Confirmation-first: never auto-verify on mount. Email security gateways
  // (common for military/gov inboxes) pre-fetch links; requiring a human click
  // prevents them from silently consuming the one-time token.
  const [state, setState] = useState(token ? VERIFY_STATE_IDLE : VERIFY_STATE_MISSING);

  const handleVerify = async () => {
    if (!token) {
      setState(VERIFY_STATE_MISSING);
      return;
    }
    setState(VERIFY_STATE_LOADING);
    try {
      await authAPI.verifyEmail(token);
      setState(VERIFY_STATE_SUCCESS);
      // Refresh cached user so gated routes unlock without a re-login.
      try {
        await refreshUser();
      } catch (refreshError) {
        // Verification already succeeded; a refresh failure is non-blocking.
      }
    } catch (error) {
      setState(mapVerifyErrorToState(error));
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12"
      data-testid="verify-email-page"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-signal-blue rounded-sm flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold text-slate-800">
            TLA<span className="text-military-gold">HOUSING</span>
          </span>
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          {state === VERIFY_STATE_MISSING && (
            <div data-testid="verify-state-missing">
              <div className="w-16 h-16 bg-amber-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                {t('verify_email.missing_title', 'Verification link incomplete')}
              </h1>
              <p className="text-slate-600 text-sm mb-6">
                {t(
                  'verify_email.missing_body',
                  'This link is missing its verification token. Please open the most recent verification email and use the link there.',
                )}
              </p>
              <Link to="/verify-email-pending">
                <Button className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white">
                  {t('verify_email.resend_cta', 'Resend verification email')}
                </Button>
              </Link>
            </div>
          )}

          {(state === VERIFY_STATE_IDLE || state === VERIFY_STATE_LOADING) && (
            <div data-testid="verify-state-idle">
              <div className="w-16 h-16 bg-signal-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-signal-blue" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                {t('verify_email.title', 'Verify your email address')}
              </h1>
              <p className="text-slate-600 text-sm mb-6">
                {t('verify_email.body', 'Click below to complete verification.')}
              </p>
              <Button
                onClick={handleVerify}
                disabled={state === VERIFY_STATE_LOADING}
                className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm py-5"
                data-testid="verify-email-button"
              >
                {state === VERIFY_STATE_LOADING ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('verify_email.verifying', 'Verifying...')}
                  </>
                ) : (
                  t('verify_email.button', 'Verify email')
                )}
              </Button>
            </div>
          )}

          {state === VERIFY_STATE_SUCCESS && (
            <div data-testid="verify-state-success">
              <div className="w-16 h-16 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                {t('verify_email.success_title', 'Email verified')}
              </h1>
              <p className="text-slate-600 text-sm mb-6">
                {t(
                  'verify_email.success_body',
                  'Your email address is confirmed. You now have full access to your account.',
                )}
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white"
                data-testid="verify-success-continue"
              >
                {t('verify_email.continue', 'Continue')}
              </Button>
            </div>
          )}

          {state === VERIFY_STATE_INVALID && (
            <div data-testid="verify-state-invalid">
              <div className="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                {t('verify_email.invalid_title', 'Link invalid or expired')}
              </h1>
              <p className="text-slate-600 text-sm mb-6">
                {t(
                  'verify_email.invalid_body',
                  'This verification link is invalid, has expired, or was already used. Request a new one to continue.',
                )}
              </p>
              <Link to="/verify-email-pending">
                <Button className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white">
                  {t('verify_email.resend_cta', 'Resend verification email')}
                </Button>
              </Link>
            </div>
          )}

          {state === VERIFY_STATE_ERROR && (
            <div data-testid="verify-state-error">
              <div className="w-16 h-16 bg-amber-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <MailCheck className="w-8 h-8 text-amber-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                {t('verify_email.error_title', 'Something went wrong')}
              </h1>
              <p className="text-slate-600 text-sm mb-6">
                {t(
                  'verify_email.error_body',
                  'We could not reach the server to verify your email. Please check your connection and try again.',
                )}
              </p>
              <Button
                onClick={handleVerify}
                className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white"
                data-testid="verify-error-retry"
              >
                {t('verify_email.retry', 'Try again')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
